import { combineClass, watch, type JSX } from 'cdui-js';
import { Show, createMemo, createSignal, mergeProps } from 'solid-js';
import './difi-img.css';
import { ErrorImageIcon } from '../../../pages/mobile/icons';
import { DiFiPreview } from '../DiFiPreview';

const ERROR_IMAGE = () => <ErrorImageIcon />;
const DEFAULT_PREVIEWABLE = false;// 默认不开启预览

export type DiFiImgProps = {
  src?: string | null;
  alt?: string;
  class?: string;
  style?: JSX.CSSProperties;
  /** 加到 <img> 上的 class */
  imgClass?: string;
  /** 失败或 src 为空时显示的图标 name */
  fallbackIcon?: string;
  /** 失败时图标上的 class */
  fallbackClass?: string;
  /** object-fit，默认 cover */
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  onLoad?: (e: Event) => void;
  onError?: (e: Event) => void;
  loading?: 'eager' | 'lazy';
  decoding?: 'async' | 'auto' | 'sync';
  referrerpolicy?: JSX.HTMLReferrerPolicy;
  crossorigin?: 'anonymous' | 'use-credentials' | '';
  errorIcon?: JSX.Element;
  /**
   * 为 true 时点击图片打开大图预览（DiFiPreview）。
   * 默认 true；设为 false 关闭预览。
   */
  previewable?: boolean;
  /**
   * 预览用图片地址；不传则使用 `src`（缩略图 + 原图场景可分开传）。
   */
  previewSrc?: string | null;
};

const isValidSrc = (src: string | null | undefined) =>
  typeof src === 'string' && src.trim().length > 0;

export const DiFiImg = (rawProps: DiFiImgProps) => {
  const props = mergeProps({ previewable: DEFAULT_PREVIEWABLE }, rawProps);
  const [failed, setFailed] = createSignal(false);
  const [previewOpen, setPreviewOpen] = createSignal(false);

  watch(
    () => [props.src, props.previewSrc],
    () => {
      setFailed(false);
      setPreviewOpen(false);
    },
  );

  const previewUrl = createMemo(() => {
    if (isValidSrc(props.previewSrc)) return String(props.previewSrc).trim();
    if (isValidSrc(props.src)) return String(props.src).trim();
    return '';
  });

  const canPreview = createMemo(
    () => props.previewable && !failed() && previewUrl() !== '',
  );

  const mainView = createMemo(() => {
    const valid = isValidSrc(props.src);
    const showFallback = !valid || failed();
    const ariaNoSrc = props.alt ? `${props.alt}（无可用图片地址）` : '无可用图片地址';
    const ariaFailed = props.alt ? `${props.alt}（加载失败）` : '图片加载失败';

    if (showFallback) {
      return (
        <div
          class={combineClass('difi-img difi-img--fallback', props.class ?? '', 'relative')}
          style={props.style}
          role="img"
          aria-label={valid && failed() ? ariaFailed : ariaNoSrc}
        >
          {props.errorIcon ? props.errorIcon : <ERROR_IMAGE />}
        </div>
      );
    }

    const openPreview = () => {
      if (props.previewable) setPreviewOpen(true);
    };

    return (
      <div
        class={combineClass(
          'difi-img',
          props.class ?? '',
          props.previewable && 'difi-img--previewable',
        )}
        style={props.style}
      >
        <img
          class={combineClass('difi-img__native', props.imgClass ?? '')}
          style={{ 'object-fit': props.objectFit ?? 'cover' }}
          src={props.src as string}
          alt={props.alt ?? ''}
          loading={props.loading}
          decoding={props.decoding}
          referrerpolicy={props.referrerpolicy || 'no-referrer'}
          crossorigin={props.crossorigin}
          onLoad={(e) => props.onLoad?.(e)}
          onError={(e) => {
            setFailed(true);
            props.onError?.(e);
          }}
          onclick={openPreview}
          role={props.previewable ? 'button' : undefined}
          tabindex={props.previewable ? 0 : undefined}
          onkeydown={(e: KeyboardEvent) => {
            if (!props.previewable) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setPreviewOpen(true);
            }
          }}
        />
      </div>
    );
  });

  return (
    <>
      {mainView() as unknown as JSX.Element}
      <Show when={canPreview()}>
        <DiFiPreview
          visible={previewOpen()}
          src={previewUrl()}
          onClose={() => setPreviewOpen(false)}
        />
      </Show>
    </>
  ) as unknown as JSX.Element;
};
